import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Crown, Shuffle, Loader2, Play } from "lucide-react";
import api from "@/lib/api";
import RemixChallengeBanner from "@/components/RemixChallengeBanner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

export default function ChallengeHistory() {
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.get("/challenges/history").then((r) => setHistory(r.data.history || [])).catch(() => setHistory([]));
  }, []);

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="challenge-history-page">
      <div className="flex items-center gap-3 mb-2">
        <Trophy className="w-8 h-8 text-amber-400" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Remix Hall of Fame</h1>
      </div>
      <p className="text-[hsl(var(--muted-foreground))] mb-6">Every week we feature a 3D character — the community remixes it, and the most-played creation is crowned champion.</p>

      <RemixChallengeBanner />

      {history === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
      ) : history.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center" data-testid="history-empty">
          <Crown className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
          <h3 className="font-display font-bold text-lg">No champions yet</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">The first winner will be crowned when this week's challenge ends. Enter above to compete!</p>
        </div>
      ) : (
        <>
          <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /> Past champions</h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="history-list">
            {history.map((h) => (
              <div key={h.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden" data-testid={`history-${h.id}`}>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[hsl(var(--border))] bg-black/10">
                  <div className="w-11 h-11 rounded-lg overflow-hidden bg-black/20 shrink-0">
                    {h.character_thumbnail ? <img src={img(h.character_thumbnail)} alt={h.character_name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center">🎮</div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{h.title}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                      <span>{fmtDate(h.ended_at)}</span>
                      <span className="flex items-center gap-1"><Shuffle className="w-3 h-3" /> {h.entries_count} entries</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => navigate(`/app/arcade/${h.winner.id}`)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition-colors group" data-testid={`history-winner-${h.id}`}>
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-amber-500/20 to-orange-500/10 grid place-items-center text-3xl shrink-0">
                    {h.winner.cover_image ? <img src={img(h.winner.cover_image)} alt="" className="w-full h-full object-cover" /> : h.winner.cover_emoji}
                    <span className="absolute -top-1 -left-1 text-lg">🏆</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold text-amber-400 uppercase tracking-wide flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> Champion</div>
                    <div className="font-bold truncate group-hover:text-[hsl(var(--primary))] transition-colors">{h.winner.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">by {h.winner.owner_name} · {h.winner.plays} plays</div>
                  </div>
                  <Play className="w-5 h-5 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] shrink-0" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
